// +----------------------------------------------------------------------
// | EB [ EB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016-2026 https://www.eb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed EB并不是自由软件，未经许可不能去掉EB相关版权
// +----------------------------------------------------------------------
// | Author: EB Team <admin@eb.com>
// +----------------------------------------------------------------------
/**
 * 平台端 user 模块（Pinia）
 * 对应 PHP/Vue2: EB view/admin/src/store/modules/user.js
 */
import {
  login as loginApi,
  logout as logoutApi,
  getBaseInfo,
  getMenusApi,
} from '@/api/user';
import { isLoginApi } from '@/api/sms';
import {
  getToken,
  setToken,
  removeToken,
  removeStorageAccount,
  setStorageAccount,
} from '@/utils/auth';
import router from '@/router';
import Cookies from 'js-cookie';
import { formatFlatteningRoutes } from '@/utils/system';
import { defineStore } from 'pinia';
import useCrmebMenuStore from '@/store/modules/menu';
import useTagsViewStore from '@/store/modules/tagsView';
import {
  normalizeRegionList,
  resolveAgentZoneId,
  type CrmebRegionItem,
} from '@/utils/crmebAdminRegion';
import type { CrmebMenuNode } from '@/utils/crmebMenu';

const ZONE_ID_KEY = 'current_zone_id';
const ONE_LV_ROUTES_KEY = 'oneLvRoutes';
const MENU_LIST_KEY = 'MenuList';

function safeParse<T>(value: string | null): T | undefined {
  try {
    return value ? (JSON.parse(value) as T) : undefined;
  } catch {
    return undefined;
  }
}

function readAdminInfo(): Record<string, unknown> | null {
  return safeParse<Record<string, unknown>>(localStorage.getItem('AdminInfo')) ?? null;
}

function initZoneId(adminInfo: Record<string, unknown> | null): number {
  let zoneId = Number(localStorage.getItem(ZONE_ID_KEY)) || 0;
  if (!adminInfo) return zoneId;
  const isAgent = Number(adminInfo.isAgent) > 0;
  const regions = normalizeRegionList(adminInfo);
  if (isAgent && regions.length) {
    if (!regions.some((r) => r.circleId === zoneId)) {
      zoneId = regions[0].circleId;
    }
  }
  return zoneId;
}

function readOneLvRoutes(): unknown[] {
  return safeParse<unknown[]>(localStorage.getItem(ONE_LV_ROUTES_KEY)) ?? [];
}

function readMenuList(): unknown[] | null {
  return safeParse<unknown[]>(localStorage.getItem(MENU_LIST_KEY)) ?? null;
}

function normalizeAdminRecord(admin: Record<string, unknown>): Record<string, unknown> {
  const regions = normalizeRegionList(admin);
  return {
    ...admin,
    ...(regions.length ? { regionName: regions } : {}),
  };
}

const bootAdminInfo = readAdminInfo();

export interface UserState {
  token?: string;
  name: string;
  avatar: string;
  introduction: string;
  roles: string[];
  zoneId: number;
  adminInfo: Record<string, unknown> | null;
  menuList: unknown[] | null;
  /** 短信服务登录态（Cookie isLogin，对应 EB state.isLogin） */
  isLoginCookie: string | undefined;
  sidebarWidth: string | null;
  sidebarStyle: string | null;
  oneLvMenus: unknown[];
  oneLvRoutes: unknown[];
  childMenuList: unknown[];
  /** EB access 权限码（按钮级，来自 profile.access） */
  crmebAccess: string[];
  /** EB 管理员 level：0 主账号，>0 子账号 */
  crmebAdminLevel: number;
}

const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: getToken(),
    name: '',
    avatar: '',
    introduction: '',
    roles: [],
    zoneId: initZoneId(bootAdminInfo),
    adminInfo: bootAdminInfo,
    menuList: readMenuList(),
    isLoginCookie: Cookies.get('isLogin'),
    sidebarWidth: window.localStorage.getItem('sidebarWidth'),
    sidebarStyle: window.localStorage.getItem('sidebarStyle'),
    oneLvMenus: [],
    oneLvRoutes: readOneLvRoutes(),
    childMenuList: [],
    crmebAccess: [],
    crmebAdminLevel: 0,
  }),
  getters: {
    /** 角色类型（对应 EB getter roleType） */
    roleType(state) {
      if (!state.adminInfo) return undefined;
      return state.adminInfo.isAgent;
    },
    /** 是否为区域代理商 or 商户管理员 */
    isAgent(state): boolean {
      const admin = state.adminInfo;
      return !!(admin && Number(admin.isAgent) > 0);
    },
    /** 是否为商户管理员 */
    isMerAdmin(state): boolean {
      const admin = state.adminInfo;
      return !!(admin && Number(admin.isAgent) === 2);
    },
    /** 是否为区域代理 */
    isZoneAgent(state): boolean {
      const admin = state.adminInfo;
      return !!(admin && Number(admin.isAgent) === 1);
    },
    /** 区域代理下辖区域列表 or 商户管理员下辖商户列表 */
    zoneList(): CrmebRegionItem[] {
      const admin = (this as ReturnType<typeof useUserStore>).adminInfo ?? readAdminInfo();
      return normalizeRegionList(admin);
    },
    /** 区域 id -> 区域信息映射（对应 EB getter zoneMap） */
    zoneMap(): Record<number, Record<string, unknown>> {
      const admin = (this as ReturnType<typeof useUserStore>).adminInfo ?? readAdminInfo();
      if (!admin) return {};
      const raw = admin.regionName;
      if (!Array.isArray(raw)) return {};
      return raw.reduce<Record<number, Record<string, unknown>>>((acc, item) => {
        const circleId = Number((item as Record<string, unknown>).circleId ?? 0);
        if (circleId) {
          acc[circleId] = item as Record<string, unknown>;
        }
        return acc;
      }, {});
    },
    /** 真实姓名 */
    realName(state): string {
      if (!state.adminInfo) return '';
      return String(state.adminInfo.realName ?? '');
    },
    /** 区域名称 */
    zoneName(): string {
      const store = this as ReturnType<typeof useUserStore>;
      const zoneMap = store.zoneMap;
      const zid = store.zoneId;
      if (!zid) return '';
      const zoneInfo = zoneMap[zid];
      return zoneInfo ? String(zoneInfo.name ?? '') : '';
    },
    /** 代理 id or 商户管理员 id */
    agentId(): number | string | null {
      const store = this as ReturnType<typeof useUserStore>;
      const zoneMap = store.zoneMap;
      const zid = store.zoneId;
      if (!zid) return null;
      const zoneInfo = zoneMap[zid];
      if (!zoneInfo) return null;
      const id = zoneInfo.circleAgentId;
      if (id === '' || id === undefined || id === null) return null;
      return id as number | string;
    },
    /** 菜单 path -> 节点映射 */
    menuMap(state): Record<string, unknown> {
      const menuMap: Record<string, unknown> = {};
      const build = (list: unknown[] | null | undefined) => {
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          const node = item as Record<string, unknown>;
          if (node.path) {
            menuMap[String(node.path)] = node;
          }
          if (Array.isArray(node.children) && node.children.length) {
            build(node.children as unknown[]);
          }
        });
      };
      build(state.menuList);
      return menuMap;
    },
    /** 短信服务是否登录（对应 EB root getter isLogin） */
    isLogin: (state) => state.isLoginCookie,
  },
  actions: {
    setAdminInfo(admin: Record<string, unknown> | null) {
      if (!admin) {
        this.adminInfo = null;
        try {
          localStorage.removeItem('AdminInfo');
        } catch {
          // ignore
        }
        return;
      }
      const normalized = normalizeAdminRecord(admin);
      this.adminInfo = normalized;
      try {
        localStorage.setItem('AdminInfo', JSON.stringify(normalized));
        setStorageAccount(JSON.stringify(normalized));
      } catch {
        // ignore
      }
    },

    setZoneId(zoneId: number) {
      this.zoneId = zoneId;
      try {
        localStorage.setItem(ZONE_ID_KEY, String(zoneId));
      } catch {
        // ignore
      }
    },

    setMenuList(menuList: unknown[]) {
      this.menuList = menuList;
      try {
        localStorage.setItem(MENU_LIST_KEY, JSON.stringify(menuList));
      } catch {
        // ignore
      }
    },

    setOneLvMenus(oneLvMenus: unknown[]) {
      this.oneLvMenus = oneLvMenus;
    },

    setOneLvRoute(oneLvRoutes: unknown[]) {
      this.oneLvRoutes = oneLvRoutes;
      try {
        localStorage.setItem(ONE_LV_ROUTES_KEY, JSON.stringify(oneLvRoutes));
      } catch {
        // ignore
      }
    },

    setChildMenuList(list: unknown[]) {
      this.childMenuList = list;
    },

    setSidebarWidth(width: string) {
      this.sidebarWidth = width;
    },

    setSidebarStyle(style: string) {
      this.sidebarStyle = style;
      window.localStorage.setItem('sidebarStyle', style);
    },

    /**
     * 用户登录
     * 对应 EB: user/login
     */
    login(userInfo: Record<string, unknown>) {
      return loginApi(userInfo).then((response: any) => {
        const data = (response?.data ?? response) as Record<string, unknown>;
        const admin = data.admin as Record<string, unknown> | undefined;
        if (admin) {
          const regions = normalizeRegionList(admin);
          if (Number(admin.isAgent) > 0 && !regions.length) {
            return Promise.reject(new Error('请先联系平台管理员绑定区域!'));
          }
        }
        const token = String(data.token ?? '');
        this.token = token;
        setToken(token);
        if (admin) {
          Cookies.set('AdminName', String(admin.account ?? ''));
          this.setAdminInfo(admin);
          if (Number(admin.isAgent) > 0) {
            this.setZoneId(resolveAgentZoneId(this.adminInfo!));
          }
          this.name = String(admin.account ?? admin.realName ?? '');
        }
        this.roles = ['crmeb'];
        return data;
      });
    },

    /**
     * 短信是否登录
     * 对应 EB: user/isLogin（Pinia 下 action 不可与 getter isLogin 同名）
     */
    fetchSmsLogin() {
      return isLoginApi()
        .then((res: any) => {
          const status = res?.data?.status ?? res?.status;
          this.isLoginCookie = status;
          Cookies.set('isLogin', String(status));
          return res;
        })
        .catch((err) => {
          this.isLoginCookie = undefined;
          Cookies.remove('isLogin');
          return Promise.reject(err);
        });
    },

    /**
     * 拉取侧栏菜单
     * 对应 EB: user/getMenus
     */
    getMenus() {
      const params: { circleId?: number } = {};
      if (this.isAgent && this.zoneId) {
        params.circleId = this.zoneId;
      }
      return getMenusApi(params).then((response: any) => {
        const payload = response?.data ?? response;
        const menuTree = (Array.isArray(payload) ? payload : []) as CrmebMenuNode[];
        this.setMenuList(menuTree);
        useCrmebMenuStore().setMenuTree(menuTree);

        const flatRoutes = router.getRoutes().map((item) => ({ ...item }));
        const oneLvMenus =
          (formatFlatteningRoutes(flatRoutes as unknown[]) as unknown[]) || [];
        const routes =
          (formatFlatteningRoutes(menuTree as unknown[]) as unknown[]) || [];
        this.setOneLvMenus(oneLvMenus);
        this.setOneLvRoute(routes);
        return response;
      });
    },

    /**
     * 拉取管理员 profile（含按钮 access）
     * 对应 EB 登录后会话中的 profile；Nest 端 GET profile
     */
    loadProfile() {
      const params: { circleId?: number } = {};
      if (this.isAgent && this.zoneId) {
        params.circleId = this.zoneId;
      }
      return getBaseInfo(params)
        .then((res: any) => {
          const info = (res?.data ?? res) as Record<string, unknown> | null;
          if (!info) {
            return info;
          }
          if (info.level !== undefined && info.level !== null) {
            this.crmebAdminLevel = Number(info.level);
          }
          const access = info.access ?? [];
          if (Array.isArray(access)) {
            this.crmebAccess = access as string[];
            useCrmebMenuStore().setAccess(this.crmebAccess);
          }
          this.name = String(info.account ?? info.realName ?? this.name);
          const regions = normalizeRegionList(info);
          const prev = this.adminInfo ?? readAdminInfo() ?? {};
          const merged = normalizeAdminRecord({
            ...prev,
            ...info,
            access: this.crmebAccess,
            ...(regions.length ? { regionName: regions } : {}),
          });
          this.setAdminInfo(merged);
          this.roles = ['crmeb'];
          return info;
        })
        .catch(() => {
          try {
            const admin = readAdminInfo();
            if (admin) {
              if (admin.level !== undefined && admin.level !== null) {
                this.crmebAdminLevel = Number(admin.level);
              }
              const access = admin.access;
              if (Array.isArray(access)) {
                this.crmebAccess = access as string[];
                useCrmebMenuStore().setAccess(this.crmebAccess);
              }
              this.name = String(admin.account ?? admin.realName ?? this.name);
              this.adminInfo = normalizeAdminRecord(admin);
            }
          } catch {
            // ignore
          }
          return null;
        });
    },

    /** 登录后会话初始化：profile + 菜单（access 先于菜单过滤） */
    initCrmebSession() {
      return this.loadProfile()
        .then(() => this.getMenus())
        .then(() => {
          this.roles = ['crmeb'];
          return true;
        });
    },

    clearSession() {
      this.token = '';
      this.roles = [];
      this.name = '';
      this.avatar = '';
      this.introduction = '';
      this.zoneId = 0;
      this.adminInfo = null;
      this.menuList = null;
      this.isLoginCookie = undefined;
      this.oneLvMenus = [];
      this.oneLvRoutes = [];
      this.childMenuList = [];
      this.crmebAccess = [];
      this.crmebAdminLevel = 0;
      removeToken();
      removeStorageAccount();
      useCrmebMenuStore().clear();
      useTagsViewStore().delAllViews();
      Cookies.remove('AdminName');
      Cookies.remove('isLogin');
      try {
        localStorage.removeItem(ZONE_ID_KEY);
        localStorage.removeItem(ONE_LV_ROUTES_KEY);
        localStorage.removeItem('AdminInfo');
        localStorage.removeItem(MENU_LIST_KEY);
      } catch {
        // ignore
      }
    },

    /**
     * 退出登录
     * 对应 EB: user/logout
     */
    logout() {
      return logoutApi()
        .then(() => {
          this.clearSession();
          try {
            localStorage.clear();
          } catch {
            // ignore
          }
        })
        .catch(() => {
          this.clearSession();
          try {
            localStorage.clear();
          } catch {
            // ignore
          }
        });
    },

    /**
     * 清除 token
     * 对应 EB: user/resetToken
     */
    resetToken() {
      this.token = '';
      this.roles = [];
      removeToken();
    },
  },
});

export default useUserStore;
